import React, { useEffect, useState } from "react";
import {
  Search,
  TrendingUp,
  ShieldCheck,
  Users,
  ArrowRight,
  Briefcase,
  ShoppingCart,
  UserPlus,
  X,
  Edit,
  Save,
  Plus,
  Trash2,
  PlusCircle,
} from "lucide-react";
import Button from "../components/UI/Button";
import BusinessCard from "../components/BusinessCard";
import { api } from "../services/api";
import { Listing, SiteContent } from "../types";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import EditableContent from "../components/Admin/EditableContent";

// Helper to map icon names to components
const IconMap: any = {
  Search: Search,
  TrendingUp: TrendingUp,
  ShieldCheck: ShieldCheck,
  Users: Users,
  Briefcase: Briefcase,
  ShoppingCart: ShoppingCart,
  UserPlus: UserPlus,
};

const HomePage: React.FC = () => {
  const [featuredListings, setFeaturedListings] = useState<Listing[]>([]);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchType, setSearchType] = useState("sale");
  const [showWelcome, setShowWelcome] = useState(true);

  // Content State
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const allListings = await api.getListings();
      // Only show approved listings in public page
      const approved = allListings.filter(l => l.status === 'approved');
      setFeaturedListings(approved.slice(0, 4));

      const pageContent = await api.getPageContent();
      setContent(pageContent);
    };
    fetchData();
  }, []);

  const handleSearch = () => {
    navigate(`/listings?type=${searchType}`);
  };

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    await api.updatePageContent(content);
    setSaving(false);
    setIsEditing(false);
  };

  const updateHomeContent = (
    section: keyof SiteContent["home"],
    key: string,
    value: any,
  ) => {
    if (!content) return;
    setContent({
      ...content,
      home: {
        ...content.home,
        [section]: {
          ...(content.home[section] || {}),
          [key]: value,
        },
      },
    });
  };

  const updateStep = (index: number, field: string, value: string) => {
    if (!content) return;
    const newSteps = [...content.home.howItWorks.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };

    setContent({
      ...content,
      home: {
        ...content.home,
        howItWorks: {
          ...content.home.howItWorks,
          steps: newSteps,
        },
      },
    });
  };

  const addStep = () => {
    if (!content) return;
    const newStep = {
      title: "مرحله جدید",
      desc: "توضیحات مرحله جدید...",
      icon: "Search",
    };
    setContent({
      ...content,
      home: {
        ...content.home,
        howItWorks: {
          ...content.home.howItWorks,
          steps: [...content.home.howItWorks.steps, newStep],
        },
      },
    });
  };

  const removeStep = (index: number) => {
    if (!content) return;
    const newSteps = content.home.howItWorks.steps.filter(
      (_, i) => i !== index,
    );
    setContent({
      ...content,
      home: {
        ...content.home,
        howItWorks: {
          ...content.home.howItWorks,
          steps: newSteps,
        },
      },
    });
  };

  if (!content || !content.home) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-600"></div>
    </div>
  );

  const isAdmin = user?.role === "admin";

  return (
    <div className="flex flex-col gap-16 pb-16 relative">
      {/* Admin Toolbar */}
      {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2">
          {isEditing ? (
            <>
              <Button
                onClick={handleSaveContent}
                disabled={saving}
                className="shadow-xl bg-green-600 hover:bg-green-700 flex items-center gap-2"
              >
                <Save size={18} /> {saving ? "ذخیره..." : "ذخیره تغییرات"}
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  window.location.reload();
                }}
                variant="ghost"
                className="bg-white shadow-xl text-red-500 hover:bg-red-50"
              >
                <X size={18} />
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setIsEditing(true)}
              className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2"
            >
              <Edit size={18} /> ویرایش صفحه
            </Button>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-[#f4f6f9] text-gray-900 py-16 lg:py-24 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-10 w-64 h-64 border-[1px] border-gray-200 rounded-full opacity-50"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 border-[1px] border-gray-200 rounded-full opacity-50"></div>
          <div className="absolute top-20 left-20 w-8 h-8 border-2 border-orange-400 rotate-45 opacity-40"></div>
          <div className="absolute bottom-32 right-32 w-4 h-4 bg-blue-800 rounded-full opacity-40"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {isAuthenticated && user && showWelcome && (
            <div className="mb-6 inline-block animate-[fadeIn_0.5s_ease-out]">
              <div className="bg-white border border-gray-200 text-gray-700 pl-2 pr-4 py-1.5 rounded-full text-sm font-medium shadow-sm flex items-center gap-3">
                <span className="flex items-center gap-2">
                  👋 خوش آمدید، {user.name}
                </span>
                <button
                  onClick={() => setShowWelcome(false)}
                  className="hover:bg-gray-100 p-1 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                  title="بستن"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight text-[#1e3a5f]">
            <EditableContent
              isEditing={isEditing}
              value={
                content.home.hero.titleLine2 || "ارزش‌گذاری و فروش کسب‌وکار"
              }
              onChange={(val) => updateHomeContent("hero", "titleLine2", val)}
            />
          </h1>

          <div className="text-lg md:text-xl text-gray-600 mb-12 max-w-2xl mx-auto font-medium">
            <EditableContent
              isEditing={isEditing}
              type="textarea"
              value={
                content.home.hero.subtitle ||
                "سامانه ارزش‌گذاری، خرید و فروش کسب‌وکارها، برندها و آگهی‌های نامشهود"
              }
              onChange={(val) => updateHomeContent("hero", "subtitle", val)}
              className="text-center bg-transparent border-gray-300 text-gray-900"
            />
          </div>

          {/* Search Bar */}
          <div className="bg-white p-2 rounded-2xl shadow-lg border border-gray-100 mb-10 flex flex-col md:flex-row gap-2 items-center">
            <div className="flex-1 w-full relative flex items-center">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="bg-transparent text-gray-700 px-4 py-3 outline-none border-none text-sm w-full appearance-none"
              >
                <option value="sale">نوع آگهی، صنعت...</option>
                <option value="demand">آگهی‌های خرید</option>
                <option value="investment">جذب سرمایه</option>
              </select>
              <div className="absolute left-4 pointer-events-none text-gray-400">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.9201 8.9502L13.4001 15.4702C12.6301 16.2402 11.3701 16.2402 10.6001 15.4702L4.08008 8.9502"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="hidden md:block w-px h-10 bg-gray-200 mx-2"></div>
            <div className="flex-1 w-full relative">
              <input
                type="text"
                placeholder="محدوده قیمت..."
                className="w-full bg-transparent text-gray-900 px-4 py-3 outline-none text-sm"
              />
            </div>
            <button
              onClick={handleSearch}
              className="w-full md:w-auto bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-8 py-3 transition-colors flex items-center justify-center"
            >
              <Search size={20} />
            </button>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            {isEditing ? (
              <div className="w-64">
                <EditableContent
                  isEditing={true}
                  value={content.home.hero.ctaText || "ثبت آگهی فروش"}
                  onChange={(val) => updateHomeContent("hero", "ctaText", val)}
                  className="text-center font-bold p-3 rounded-xl text-gray-900 border border-gray-300 bg-white"
                  placeholder="متن دکمه (مثلا: ثبت آگهی فروش)"
                />
              </div>
            ) : (
              <>
                <Button
                  size="lg"
                  onClick={() => navigate("/create-listing")}
                  className="px-8 py-3.5 text-base bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl transition-transform hover:scale-105 shadow-lg shadow-orange-500/20"
                >
                  {content.home.hero.ctaText || "ثبت آگهی فروش"}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/create-listing?type=demand")}
                  className="px-8 py-3.5 text-base border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-blue-50 rounded-xl transition-transform hover:scale-105"
                >
                  درخواست خرید
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features Section (Replacing How it works) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Feature 1 */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-16 h-16 bg-blue-50 text-blue-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <TrendingUp size={32} />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              ارزش‌گذاری دقیق
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              تعیین ارزش واقعی دارایی‌ها توسط کارشناسان رسمی
            </p>
          </div>
          {/* Feature 2 */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Briefcase size={32} />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              فهرست متنوع آگهی‌ها
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              دسترسی به هزاران برند، اختراع، لایسنس و کسب‌وکارهای فعال
            </p>
          </div>
          {/* Feature 3 */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={32} />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              امنیت و محرمانگی
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              تضمین امنیت معاملات و حفظ حریم خصوصی اطلاعات شما.
            </p>
          </div>
          {/* Feature 4 */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
            <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users size={32} />
            </div>
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              مشاوره تخصصی
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              راهنمایی فنی و حقوقی در تمام مراحل معامله
            </p>
          </div>
        </div>
      </section>

      {/* Successful Projects Section */}
      {(content.home.successfulProjects?.isActive || isEditing) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="text-center mb-12">
            {isEditing ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.home.successfulProjects?.isActive || false}
                      onChange={(e) => {
                        if (!content.home.successfulProjects) {
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              successfulProjects: {
                                title: "پروژه‌های موفق",
                                subtitle: "نمونه‌هایی از پروژه‌های موفق انجام شده",
                                isActive: e.target.checked,
                                projects: []
                              }
                            }
                          });
                        } else {
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              successfulProjects: {
                                ...content.home.successfulProjects,
                                isActive: e.target.checked
                              }
                            }
                          });
                        }
                      }}
                      className="w-5 h-5 rounded text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 font-medium">نمایش بخش پروژه‌های موفق</span>
                  </label>
                </div>
                <input
                  type="text"
                  value={content.home.successfulProjects?.title || "پروژه‌های موفق"}
                  onChange={(e) => {
                    const current = content.home.successfulProjects || { title: "", subtitle: "", isActive: false, projects: [] };
                    setContent({
                      ...content,
                      home: {
                        ...content.home,
                        successfulProjects: { ...current, title: e.target.value }
                      }
                    });
                  }}
                  className="w-full max-w-md mx-auto p-2 border rounded-lg text-center text-2xl font-bold"
                />
                <input
                  type="text"
                  value={content.home.successfulProjects?.subtitle || ""}
                  onChange={(e) => {
                    const current = content.home.successfulProjects || { title: "", subtitle: "", isActive: false, projects: [] };
                    setContent({
                      ...content,
                      home: {
                        ...content.home,
                        successfulProjects: { ...current, subtitle: e.target.value }
                      }
                    });
                  }}
                  className="w-full max-w-2xl mx-auto p-2 border rounded-lg text-center"
                  placeholder="توضیحات کوتاه..."
                />
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-[#1e3a5f] mb-3">
                  {content.home.successfulProjects?.title || "پروژه‌های موفق"}
                </h2>
                {content.home.successfulProjects?.subtitle && (
                  <p className="text-gray-600 text-lg">
                    {content.home.successfulProjects.subtitle}
                  </p>
                )}
              </>
            )}
          </div>

          {isEditing && (
            <div className="mb-6 text-center">
              <button
                onClick={() => {
                  const current = content.home.successfulProjects || { title: "", subtitle: "", isActive: false, projects: [] };
                  const newProject = {
                    id: Date.now().toString(),
                    title: "عنوان پروژه",
                    description: "توضیحات پروژه",
                    imageUrl: "https://via.placeholder.com/400x300",
                    category: "دسته‌بندی"
                  };
                  setContent({
                    ...content,
                    home: {
                      ...content.home,
                      successfulProjects: {
                        ...current,
                        projects: [...current.projects, newProject]
                      }
                    }
                  });
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 mx-auto"
              >
                <Plus size={18} /> افزودن پروژه
              </button>
            </div>
          )}

          {isEditing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(content.home.successfulProjects?.projects || []).map((project) => (
                <div key={project.id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-bold text-gray-700">ویرایش پروژه</span>
                    <button
                      onClick={() => {
                        const current = content.home.successfulProjects!;
                        setContent({
                          ...content,
                          home: {
                            ...content.home,
                            successfulProjects: {
                              ...current,
                              projects: current.projects.filter(p => p.id !== project.id)
                            }
                          }
                        });
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="عنوان پروژه"
                      value={project.title}
                      onChange={(e) => {
                        const current = content.home.successfulProjects!;
                        const updatedProjects = current.projects.map(p =>
                          p.id === project.id ? { ...p, title: e.target.value } : p
                        );
                        setContent({
                          ...content,
                          home: {
                            ...content.home,
                            successfulProjects: { ...current, projects: updatedProjects }
                          }
                        });
                      }}
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="دسته‌بندی"
                      value={project.category}
                      onChange={(e) => {
                        const current = content.home.successfulProjects!;
                        const updatedProjects = current.projects.map(p =>
                          p.id === project.id ? { ...p, category: e.target.value } : p
                        );
                        setContent({
                          ...content,
                          home: {
                            ...content.home,
                            successfulProjects: { ...current, projects: updatedProjects }
                          }
                        });
                      }}
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                    <textarea
                      placeholder="توضیحات پروژه"
                      value={project.description}
                      onChange={(e) => {
                        const current = content.home.successfulProjects!;
                        const updatedProjects = current.projects.map(p =>
                          p.id === project.id ? { ...p, description: e.target.value } : p
                        );
                        setContent({
                          ...content,
                          home: {
                            ...content.home,
                            successfulProjects: { ...current, projects: updatedProjects }
                          }
                        });
                      }}
                      className="w-full p-2 border rounded-lg text-sm"
                      rows={3}
                    />
                    <input
                      type="text"
                      placeholder="آدرس تصویر (URL)"
                      value={project.imageUrl}
                      onChange={(e) => {
                        const current = content.home.successfulProjects!;
                        const updatedProjects = current.projects.map(p =>
                          p.id === project.id ? { ...p, imageUrl: e.target.value } : p
                        );
                        setContent({
                          ...content,
                          home: {
                            ...content.home,
                            successfulProjects: { ...current, projects: updatedProjects }
                          }
                        });
                      }}
                      className="w-full p-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(content.home.successfulProjects?.projects || []).map((project) => (
                <div key={project.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      {project.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">
                      {project.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {project.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(!content.home.successfulProjects?.projects || content.home.successfulProjects.projects.length === 0) && !isEditing && (
            <div className="text-center py-12 text-gray-500">
              هنوز پروژه‌ای اضافه نشده است.
            </div>
          )}
        </section>
      )}

      {/* Trusted Partners Section */}
      {(content.home.trustedPartners?.isActive || isEditing) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            {isEditing ? (
              <>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.home.trustedPartners?.isActive || false}
                      onChange={(e) => {
                        if (!content.home.trustedPartners) {
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              trustedPartners: {
                                title: "مورد اعتماد",
                                subtitle: "شرکت‌ها و سازمان‌هایی که به ما اعتماد کرده‌اند",
                                isActive: e.target.checked,
                                logos: []
                              }
                            }
                          });
                        } else {
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              trustedPartners: {
                                ...content.home.trustedPartners,
                                isActive: e.target.checked
                              }
                            }
                          });
                        }
                      }}
                      className="w-5 h-5 rounded text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-700 font-medium">نمایش بخش مورد اعتماد</span>
                  </label>
                </div>
                <div className="max-w-2xl mx-auto space-y-4 mb-6">
                  <input
                    type="text"
                    value={content.home.trustedPartners?.title || "مورد اعتماد"}
                    onChange={(e) => {
                      const current = content.home.trustedPartners || { title: "", subtitle: "", isActive: false, logos: [] };
                      setContent({
                        ...content,
                        home: {
                          ...content.home,
                          trustedPartners: { ...current, title: e.target.value }
                        }
                      });
                    }}
                    className="w-full p-3 border border-gray-300 rounded-xl text-center font-bold text-2xl"
                    placeholder="عنوان بخش"
                  />
                  <input
                    type="text"
                    value={content.home.trustedPartners?.subtitle || ""}
                    onChange={(e) => {
                      const current = content.home.trustedPartners || { title: "", subtitle: "", isActive: false, logos: [] };
                      setContent({
                        ...content,
                        home: {
                          ...content.home,
                          trustedPartners: { ...current, subtitle: e.target.value }
                        }
                      });
                    }}
                    className="w-full p-3 border border-gray-300 rounded-xl text-center"
                    placeholder="توضیحات بخش"
                  />
                </div>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-[#1e3a5f] mb-3">
                  {content.home.trustedPartners?.title || "مورد اعتماد"}
                </h2>
                {content.home.trustedPartners?.subtitle && (
                  <p className="text-gray-600 text-lg">
                    {content.home.trustedPartners.subtitle}
                  </p>
                )}
              </>
            )}
          </div>

          {isEditing ? (
            <div className="bg-gray-50 p-6 rounded-2xl border-2 border-dashed border-gray-300">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-gray-900">مدیریت لوگوها</h3>
                <button
                  onClick={() => {
                    const current = content.home.trustedPartners || { title: "", subtitle: "", isActive: false, logos: [] };
                    const newLogo = {
                      id: Date.now().toString(),
                      name: "شرکت جدید",
                      imageUrl: "",
                      websiteUrl: ""
                    };
                    setContent({
                      ...content,
                      home: {
                        ...content.home,
                        trustedPartners: {
                          ...current,
                          logos: [...current.logos, newLogo]
                        }
                      }
                    });
                  }}
                  className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-xl hover:bg-teal-700 transition-colors"
                >
                  <PlusCircle size={18} /> افزودن لوگو
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(content.home.trustedPartners?.logos || []).map((logo, index) => (
                  <div key={logo.id} className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium text-gray-500">لوگو #{index + 1}</span>
                      <button
                        onClick={() => {
                          const current = content.home.trustedPartners!;
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              trustedPartners: {
                                ...current,
                                logos: current.logos.filter(l => l.id !== logo.id)
                              }
                            }
                          });
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={logo.name}
                        onChange={(e) => {
                          const current = content.home.trustedPartners!;
                          const updatedLogos = current.logos.map(l =>
                            l.id === logo.id ? { ...l, name: e.target.value } : l
                          );
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              trustedPartners: { ...current, logos: updatedLogos }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="نام شرکت"
                      />
                      <input
                        type="text"
                        value={logo.imageUrl}
                        onChange={(e) => {
                          const current = content.home.trustedPartners!;
                          const updatedLogos = current.logos.map(l =>
                            l.id === logo.id ? { ...l, imageUrl: e.target.value } : l
                          );
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              trustedPartners: { ...current, logos: updatedLogos }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="آدرس تصویر لوگو (URL)"
                        dir="ltr"
                      />
                      <input
                        type="text"
                        value={logo.websiteUrl || ""}
                        onChange={(e) => {
                          const current = content.home.trustedPartners!;
                          const updatedLogos = current.logos.map(l =>
                            l.id === logo.id ? { ...l, websiteUrl: e.target.value } : l
                          );
                          setContent({
                            ...content,
                            home: {
                              ...content.home,
                              trustedPartners: { ...current, logos: updatedLogos }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="آدرس وبسایت (اختیاری)"
                        dir="ltr"
                      />
                      {logo.imageUrl && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg flex items-center justify-center">
                          <img
                            src={logo.imageUrl}
                            alt={logo.name}
                            className="max-h-16 max-w-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {(!content.home.trustedPartners?.logos || content.home.trustedPartners.logos.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  هنوز لوگویی اضافه نشده است. روی دکمه "افزودن لوگو" کلیک کنید.
                </div>
              )}
            </div>
          ) : (
            <div className="relative overflow-hidden">
              <div className="flex animate-scroll-logos">
                {/* Duplicate logos for seamless loop */}
                {[...content.home.trustedPartners?.logos || [], ...content.home.trustedPartners?.logos || []].map((logo, index) => (
                  <div key={`${logo.id}-${index}`} className="flex-shrink-0 w-48 px-6 flex items-center justify-center">
                    {logo.websiteUrl ? (
                      <a
                        href={logo.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                        title={logo.name}
                      >
                        <img
                          src={logo.imageUrl}
                          alt={logo.name}
                          className="max-h-20 max-w-full object-contain"
                        />
                      </a>
                    ) : (
                      <div className="grayscale opacity-60">
                        <img
                          src={logo.imageUrl}
                          alt={logo.name}
                          className="max-h-20 max-w-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Featured Listings */}
      <section className="bg-transparent py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-8">
              جدیدترین آگهی‌های ثبت شده
            </h2>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {[
                "همه",
                "صنایع غذایی",
                "صنایع شیمیایی",
                "صنایع دارویی",
                "صنایع نساجی",
                "صنایع فلزی",
                "صنایع چوب",
                "صنایع پلاستیک",
                "صنایع برق و الکترونیک",
              ].map((cat, idx) => (
                <button
                  key={idx}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${idx === 0 ? "bg-[#1e3a5f] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredListings.slice(0, 4).map((listing) => (
              <BusinessCard key={listing.id} listing={listing} />
            ))}
          </div>
        </div>
      </section>

      {/* Banner Section */}
      {(content.home.banner?.isActive || isEditing) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {isEditing ? (
            <div className="bg-gray-50 p-6 rounded-2xl border-2 border-dashed border-gray-300">
              <h3 className="font-bold mb-4 text-gray-900">
                تنظیمات بنر تبلیغاتی
              </h3>
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={content.home.banner?.isActive || false}
                    onChange={(e) =>
                      updateHomeContent("banner", "isActive", e.target.checked)
                    }
                    className="w-5 h-5 rounded text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-gray-700 font-medium">نمایش بنر</span>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    آدرس تصویر بنر (URL)
                  </label>
                  <input
                    type="text"
                    value={content.home.banner?.imageUrl || ""}
                    onChange={(e) =>
                      updateHomeContent("banner", "imageUrl", e.target.value)
                    }
                    className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-teal-500 focus:border-teal-500"
                    dir="ltr"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    لینک مقصد (URL)
                  </label>
                  <input
                    type="text"
                    value={content.home.banner?.linkUrl || ""}
                    onChange={(e) =>
                      updateHomeContent("banner", "linkUrl", e.target.value)
                    }
                    className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-teal-500 focus:border-teal-500"
                    dir="ltr"
                    placeholder="https://example.com/target-page"
                  />
                </div>
              </div>
              {content.home.banner?.imageUrl && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    پیش‌نمایش بنر:
                  </p>
                  <img
                    src={content.home.banner.imageUrl}
                    alt="Banner Preview"
                    className="w-full h-auto max-h-64 object-cover rounded-xl shadow-sm"
                  />
                </div>
              )}
            </div>
          ) : (
            content.home.banner?.isActive &&
            content.home.banner.imageUrl && (
              <a
                href={content.home.banner.linkUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all group"
              >
                <img
                  src={content.home.banner.imageUrl}
                  alt="Advertisement Banner"
                  className="w-full h-auto max-h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </a>
            )
          )}
        </section>
      )}
    </div>
  );
};

export default HomePage;
